import { daysInMonth, monthKeyFromT } from './calendar';
import {
    distributionPartnerKeys,
    distributionSubtotalKeys,
    prefReclassKeys,
} from './lineKeys';
import type {
    CashPolicy,
    CashPolicyMonthResult,
    CashPolicyOneTimeDistribution,
    CashPolicyResult,
    CashPolicySplitSchedule,
    EvaluateContext,
    LoggedDistributionRow,
    PayPeriodCadence,
    SchemaEntry,
} from './types';

export type { PayPeriodCadence };

export const PAY_PERIOD_CADENCES: readonly PayPeriodCadence[] = [
    'biweekly',
    'semimonthly',
    'weekly',
    'monthly',
];

export function parsePayPeriodParams(
    params: { cadence?: unknown; anchor_date?: unknown } | undefined,
): { cadence: PayPeriodCadence; anchor_date: string } | null {
    if (!params) {
        return null;
    }
    const cadence = params.cadence;
    const anchorDate = params.anchor_date;
    if (typeof cadence !== 'string' || typeof anchorDate !== 'string') {
        return null;
    }
    if (!(PAY_PERIOD_CADENCES as readonly string[]).includes(cadence)) {
        return null;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(anchorDate)) {
        return null;
    }
    return { cadence: cadence as PayPeriodCadence, anchor_date: anchorDate };
}

function payPeriodStep(cadence: PayPeriodCadence): number | null {
    if (cadence === 'weekly') {
        return 7;
    }
    if (cadence === 'biweekly') {
        return 14;
    }
    return null;
}

function semimonthlyDistDays(monthKey: string): number[] | null {
    const bounds = monthBounds(monthKey);
    if (bounds === null) {
        return null;
    }
    const year = Number(monthKey.slice(0, 4));
    const month = Number(monthKey.slice(5, 7));
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return [
        Date.UTC(year, month - 1, 15) / 86400000,
        Date.UTC(year, month - 1, lastDay) / 86400000,
    ];
}

const CASH_KEY = 'bs.cash_bank_accounts_and_petty_cash';
const MM_KEY = 'bs.cash_equivalents';

/** Used only when no Tier 4 shares are available, so cash and equity still move together. */
export const FALLBACK_LINE_KEY = 'bs.tier_4_distributions_partner_1';

export interface CashPolicyModelAccess {
    getNumeric: (lineKey: string, t: number) => number | null;
    setCell: (lineKey: string, t: number, value: number | null) => void;
}

export function roundCents(value: number): number {
    return Math.round(value * 100) / 100;
}

function moneyEqual(a: number, b: number): boolean {
    return Math.round(a * 100) === Math.round(b * 100);
}

function utcDayCount(dateStr: string): number | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return null;
    }
    const parts = dateStr.split('-').map(Number);
    const y = parts[0]!;
    const m = parts[1]!;
    const d = parts[2]!;
    return Date.UTC(y, m - 1, d) / 86400000;
}

function monthBounds(monthKey: string): { start: number; end: number } | null {
    if (!/^\d{4}-\d{2}$/.test(monthKey)) {
        return null;
    }
    const year = Number(monthKey.slice(0, 4));
    const month = Number(monthKey.slice(5, 7));
    const start = Date.UTC(year, month - 1, 1) / 86400000;
    const end = Date.UTC(year, month, 0) / 86400000;
    return { start, end };
}

function parseAnchorDay(anchorDate: string): number | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(anchorDate)) {
        return null;
    }
    return Number(anchorDate.slice(8, 10));
}

export function policyOwnedLineKeys(schema: SchemaEntry[]): Set<string> {
    const keys = new Set<string>([MM_KEY]);
    for (const key of distributionPartnerKeys(schema)) {
        keys.add(key);
    }
    for (const key of distributionSubtotalKeys(schema)) {
        keys.add(key);
    }
    for (const key of prefReclassKeys(schema)) {
        keys.add(key);
    }
    return keys;
}

function utcDayToIso(dayCount: number): string {
    const ms = dayCount * 86400000;
    const date = new Date(ms);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** ISO pay dates in a month (same cadence/anchor loop as distributionsInMonth). */
export function payDateListInMonth(
    cadence: PayPeriodCadence,
    anchorDate: string,
    monthKey: string,
): string[] {
    if (cadence === 'monthly') {
        const bounds = monthBounds(monthKey);
        const anchorDay = parseAnchorDay(anchorDate);
        if (bounds === null || anchorDay === null) {
            return [];
        }
        const year = Number(monthKey.slice(0, 4));
        const month = Number(monthKey.slice(5, 7));
        const dim = daysInMonth(`${year}-${String(month).padStart(2, '0')}-01`, 1);
        const day = Math.min(anchorDay, dim);
        return [`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`];
    }
    if (cadence === 'semimonthly') {
        const distDays = semimonthlyDistDays(monthKey);
        if (distDays === null) {
            return [];
        }
        return distDays.map((dayCount) => utcDayToIso(dayCount));
    }
    const step = payPeriodStep(cadence);
    if (step === null) {
        return [];
    }
    const anchorDays = utcDayCount(anchorDate);
    const bounds = monthBounds(monthKey);
    if (anchorDays === null || bounds === null) {
        return [];
    }
    const kLow = Math.ceil((bounds.start - anchorDays) / step);
    const kHigh = Math.floor((bounds.end - anchorDays) / step);
    if (kHigh < kLow) {
        return [];
    }
    const dates: string[] = [];
    for (let k = kLow; k <= kHigh; k += 1) {
        dates.push(utcDayToIso(anchorDays + step * k));
    }
    return dates;
}

export function distributionsInMonth(
    cadence: PayPeriodCadence,
    anchorDate: string,
    monthKey: string,
): number {
    if (cadence === 'monthly') {
        return 1;
    }
    if (cadence === 'semimonthly') {
        return 2;
    }
    const step = payPeriodStep(cadence);
    if (step === null) {
        return 0;
    }
    const anchorDays = utcDayCount(anchorDate);
    const bounds = monthBounds(monthKey);
    if (anchorDays === null || bounds === null) {
        return 0;
    }
    const kLow = Math.ceil((bounds.start - anchorDays) / step);
    const kHigh = Math.floor((bounds.end - anchorDays) / step);
    if (kHigh < kLow) {
        return 0;
    }
    return kHigh - kLow + 1;
}

export function normalizeOneTime(
    oneTime: CashPolicyOneTimeDistribution | null | undefined,
): CashPolicyOneTimeDistribution | null {
    if (!oneTime) {
        return null;
    }
    const { date, amount } = oneTime;
    if (!date || date.trim() === '' || amount == null || amount <= 0) {
        return null;
    }
    return { date, amount };
}

export function effectiveOneTime(
    policy: CashPolicy,
    _lastDistDate: string | null,
    lastClosedMonth: string,
): CashPolicyOneTimeDistribution | null {
    const extra = normalizeOneTime(policy.distribution.one_time);
    if (!extra) {
        return null;
    }
    const { date, amount } = extra;
    const closedMonthKey = lastClosedMonth.trim().slice(0, 7);
    if (date.slice(0, 7) <= closedMonthKey) {
        return null;
    }
    let inHorizon = false;
    for (let t = 1; t <= 36; t += 1) {
        if (monthKeyFromT(lastClosedMonth, t) === date.slice(0, 7)) {
            inHorizon = true;
            break;
        }
    }
    if (!inHorizon) {
        return null;
    }
    return { date, amount };
}

export function oneTimeInMonth(
    policy: CashPolicy,
    monthKey: string,
    lastDistDate: string | null,
    lastClosedMonth: string,
): { amount: number; date: string } | null {
    const extra = effectiveOneTime(policy, lastDistDate, lastClosedMonth);
    if (!extra || extra.date.slice(0, 7) !== monthKey) {
        return null;
    }
    return { amount: roundCents(extra.amount), date: extra.date };
}

export function policyDatesInMonth(
    cadence: PayPeriodCadence,
    anchorDate: string,
    monthKey: string,
    lastDistDate: string | null,
    skipDate: string | null = null,
): number {
    if (cadence === 'semimonthly') {
        const distDaysList = semimonthlyDistDays(monthKey);
        if (distDaysList === null) {
            return 0;
        }
        const dates = distDaysList.map((distDays) => utcDayToIso(distDays));
        const filtered = skipDate ? dates.filter((d) => d !== skipDate) : dates;
        if (lastDistDate === null) {
            return filtered.length;
        }
        const lastLogged = utcDayCount(lastDistDate);
        if (lastLogged === null) {
            return 0;
        }
        return filtered.filter((d) => {
            const days = utcDayCount(d);
            return days !== null && days > lastLogged;
        }).length;
    }
    if (cadence === 'monthly') {
        const bounds = monthBounds(monthKey);
        const anchorDay = parseAnchorDay(anchorDate);
        if (bounds === null || anchorDay === null) {
            return 0;
        }
        const year = Number(monthKey.slice(0, 4));
        const month = Number(monthKey.slice(5, 7));
        const dim = daysInMonth(`${year}-${String(month).padStart(2, '0')}-01`, 1);
        const day = Math.min(anchorDay, dim);
        const distDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        if (skipDate && distDate === skipDate) {
            return 0;
        }
        const distDays = Date.UTC(year, month - 1, day) / 86400000;
        if (lastDistDate !== null) {
            const lastLogged = utcDayCount(lastDistDate);
            if (lastLogged !== null && distDays <= lastLogged) {
                return 0;
            }
        }
        return 1;
    }
    const step = payPeriodStep(cadence);
    if (step === null) {
        return 0;
    }
    const anchorDays = utcDayCount(anchorDate);
    if (anchorDays === null) {
        return 0;
    }
    const lastLogged = lastDistDate === null ? null : utcDayCount(lastDistDate);
    const dates = payDateListInMonth(cadence, anchorDate, monthKey);
    let count = 0;
    for (const distDate of dates) {
        if (skipDate && distDate === skipDate) {
            continue;
        }
        const distDays = utcDayCount(distDate);
        if (distDays === null) {
            continue;
        }
        if (lastLogged === null) {
            count += 1;
            continue;
        }
        // When the policy anchor is after the last logged distribution, projected periods
        // start on the anchor date (same cadence as the Distributions section). Otherwise
        // skip dates on or before the last logged distribution.
        if (lastDistDate !== null && anchorDate > lastDistDate) {
            if (distDays >= anchorDays) {
                count += 1;
            }
        } else if (distDays > lastLogged) {
            count += 1;
        }
    }
    return count;
}

export function effectiveFloor(policy: CashPolicy): number {
    return roundCents(
        (policy.floor.operating_minimum ?? 0) + (policy.floor.drawdown_allowance ?? 0),
    );
}

export function scheduleIsValid(
    schedule: CashPolicySplitSchedule | null,
    policy: CashPolicy,
    t: number,
    context: EvaluateContext,
): boolean {
    if (!schedule || !policy.enabled) {
        return false;
    }
    const basis = schedule.basis;
    if (t > basis.horizon_months) {
        return false;
    }
    const amountPerPeriod = policy.distribution.amount_per_period;
    if (amountPerPeriod == null || !moneyEqual(basis.amount_per_period, amountPerPeriod)) {
        return false;
    }
    if (basis.cadence !== policy.distribution.cadence) {
        return false;
    }
    if (basis.anchor_date !== policy.distribution.anchor_date) {
        return false;
    }
    if (basis.last_dist_num !== (context.last_dist_num ?? null)) {
        return false;
    }
    if (context.waterfall_mode != null && basis.mode !== context.waterfall_mode) {
        return false;
    }
    if (basis.last_closed_month !== context.last_closed_month) {
        return false;
    }
    const policyExtra = effectiveOneTime(
        policy,
        context.last_dist_date,
        context.last_closed_month,
    );
    const basisExtraDate = basis.one_time_date ?? null;
    const basisExtraAmount = basis.one_time_amount ?? null;
    if (policyExtra) {
        if (basisExtraDate !== policyExtra.date) {
            return false;
        }
        if (basisExtraAmount == null || !moneyEqual(basisExtraAmount, policyExtra.amount)) {
            return false;
        }
    } else if (basisExtraDate != null || (basisExtraAmount != null && basisExtraAmount !== 0)) {
        return false;
    }
    return true;
}

function mergeLines(
    target: Record<string, number>,
    source: Record<string, number>,
): void {
    for (const [key, amount] of Object.entries(source)) {
        target[key] = roundCents((target[key] ?? 0) + amount);
    }
}

function sumLines(lines: Record<string, number>): number {
    return roundCents(Object.values(lines).reduce((acc, v) => acc + v, 0));
}

export function linesForMonth(
    policy: CashPolicy,
    t: number,
    policyDist: number,
    context: EvaluateContext,
    tier4Shares: Record<string, number>,
): { lines: Record<string, number>; source: 'waterfall' | 'tier4_placeholder' } {
    const schedule = policy.distribution.split_schedule;
    if (scheduleIsValid(schedule, policy, t, context)) {
        const monthEntry = schedule!.months.find((m) => m.t === t);
        if (monthEntry) {
            return { lines: monthEntry.lines, source: 'waterfall' };
        }
    }
    return { lines: placeholderLines(policyDist, tier4Shares), source: 'tier4_placeholder' };
}

export function placeholderLines(
    dist: number,
    tier4Shares: Record<string, number>,
): Record<string, number> {
    const keys = Object.keys(tier4Shares);
    if (keys.length === 0) {
        return dist === 0 ? {} : { [FALLBACK_LINE_KEY]: roundCents(dist) };
    }
    const lines: Record<string, number> = {};
    let sum = 0;
    let maxShare = -1;
    let maxKey = keys[0]!;
    for (const key of keys) {
        const share = tier4Shares[key] ?? 0;
        const amount = roundCents(dist * share);
        lines[key] = amount;
        sum += amount;
        if (share > maxShare) {
            maxShare = share;
            maxKey = key;
        }
    }
    const residue = roundCents(dist - sum);
    if (residue !== 0) {
        lines[maxKey] = roundCents((lines[maxKey] ?? 0) + residue);
    }
    return lines;
}

export function aprForMonth(policy: CashPolicy, t: number): number {
    const snapshot = policy.sweep.yield_snapshot;
    if (!snapshot || t < 1) {
        return 0;
    }
    return snapshot.apr_by_month[t - 1] ?? 0;
}

export function interestForMonth(mm0: number, apr: number, sweepEnabled: boolean): number {
    if (!sweepEnabled || !(mm0 > 0) || !(apr > 0)) {
        return 0;
    }
    return roundCents(mm0 * apr / 12);
}

function loggedRowsForMonth(
    loggedAfterClose: LoggedDistributionRow[],
    monthKey: string,
): LoggedDistributionRow[] {
    return loggedAfterClose.filter((row) => row.date.slice(0, 7) === monthKey);
}

const DISTRIBUTION_LINE_RE = /^bs\.(tmf|tier_[1-4])_distributions_partner_\d+$/;

function onlyDistributionLines(lines: Record<string, number>): boolean {
    return Object.keys(lines).every((key) => DISTRIBUTION_LINE_RE.test(key));
}

export interface CashPolicyIssue {
    code: 'policy_logged_amount_mismatch' | 'policy_schedule_mismatch';
    message: string;
}

const PREF_RECLASS_LINE_RE = /^bs\.(preferred_equity|allocated_income)_partner_\d+$/;

export function isBalancedPrefAccrualReclass(entry: { lines: Record<string, number> }): boolean {
    const keys = Object.keys(entry.lines);
    const balanced = Math.abs(sumLines(entry.lines)) < 0.005;
    return keys.every((key) => PREF_RECLASS_LINE_RE.test(key)) && balanced;
}

/** Month t's projected preferred-return reclass, or nothing when the schedule is not current. */
export function prefAccrualLinesForMonth(
    policy: CashPolicy,
    t: number,
    context: EvaluateContext,
): { lines: Record<string, number>; issue: CashPolicyIssue | null } {
    const schedule = policy.distribution.split_schedule;
    if (!scheduleIsValid(schedule, policy, t, context)) {
        return { lines: {}, issue: null };
    }
    const entry = schedule!.pref_accrual?.find((m) => m.t === t);
    if (!entry) {
        return { lines: {}, issue: null };
    }
    if (!isBalancedPrefAccrualReclass(entry)) {
        return {
            lines: {},
            issue: {
                code: 'policy_schedule_mismatch',
                message: `Preferred-return accrual for t=${t} is not a balanced Preferred Equity / Allocated Income reclass; not applied`,
            },
        };
    }
    return { lines: entry.lines, issue: null };
}

function buildMonthLines(
    policy: CashPolicy,
    t: number,
    loggedRows: LoggedDistributionRow[],
    policyDist: number,
    extraDist: number,
    context: EvaluateContext,
    tier4Shares: Record<string, number>,
): {
    lines: Record<string, number>;
    source: 'waterfall' | 'tier4_placeholder';
    logged: number;
    issues: CashPolicyIssue[];
} {
    const merged: Record<string, number> = {};
    const issues: CashPolicyIssue[] = [];
    let source: 'waterfall' | 'tier4_placeholder' = 'waterfall';
    let logged = 0;

    for (const row of loggedRows) {
        if (
            row.lines != null &&
            Object.keys(row.lines).length > 0 &&
            onlyDistributionLines(row.lines)
        ) {
            const rowTotal = sumLines(row.lines);
            mergeLines(merged, row.lines);
            logged = roundCents(logged + rowTotal);
            if (Math.abs(rowTotal - row.amount) >= 0.005) {
                issues.push({
                    code: 'policy_logged_amount_mismatch',
                    message: `Logged distribution #${row.num} amount ${row.amount} differs from its tier lines ${rowTotal}; tier lines used`,
                });
            }
        } else {
            mergeLines(merged, placeholderLines(row.amount, tier4Shares));
            logged = roundCents(logged + row.amount);
            source = 'tier4_placeholder';
        }
    }

    const policyAndExtra = roundCents(policyDist + extraDist);
    if (policyAndExtra > 0) {
        const schedule = policy.distribution.split_schedule;
        const monthEntry = scheduleIsValid(schedule, policy, t, context)
            ? schedule!.months.find((m) => m.t === t)
            : undefined;
        if (
            monthEntry &&
            onlyDistributionLines(monthEntry.lines) &&
            Math.abs(sumLines(monthEntry.lines) - policyAndExtra) < 0.005
        ) {
            mergeLines(merged, monthEntry.lines);
        } else {
            if (monthEntry) {
                issues.push({
                    code: 'policy_schedule_mismatch',
                    message: `Waterfall schedule month t=${t} totals ${sumLines(monthEntry.lines)} but the policy distributes ${policyAndExtra}; Tier 4 placeholder used`,
                });
            }
            mergeLines(merged, placeholderLines(policyAndExtra, tier4Shares));
            source = 'tier4_placeholder';
        }
    }

    return { lines: merged, source, logged, issues };
}

/**
 * Earliest-greedy excess sweeps from a no-sweep operating-cash path.
 * slack[t] = C[t] − F; suffixMin[t] = min slack from t onward; take min slack up front.
 */
export function safeExcessSweeps(operatingCashByMonth: number[], floor: number): number[] {
    const n = operatingCashByMonth.length;
    if (n === 0) {
        return [];
    }
    const slack = operatingCashByMonth.map((c) => roundCents(c - floor));
    const suffixMin = new Array<number>(n);
    let min = Infinity;
    for (let i = n - 1; i >= 0; i -= 1) {
        min = Math.min(min, slack[i] ?? 0);
        suffixMin[i] = min;
    }
    const sweeps = new Array<number>(n);
    let taken = 0;
    for (let t = 0; t < n; t += 1) {
        const s = roundCents(Math.max(0, (suffixMin[t] ?? 0) - taken));
        sweeps[t] = s;
        taken += s;
    }
    return sweeps;
}

export interface ApplyCashPolicyMonthResult {
    month: CashPolicyMonthResult;
    splitSource: 'waterfall' | 'tier4_placeholder';
    issues: CashPolicyIssue[];
}

export function applyCashPolicyMonth(
    policy: CashPolicy,
    t: number,
    lastClosedMonth: string,
    historyMmAtT0: number,
    model: CashPolicyModelAccess,
    context: EvaluateContext,
): ApplyCashPolicyMonthResult {
    const F = effectiveFloor(policy);
    const pre = model.getNumeric(CASH_KEY, t) ?? 0;
    const postPrev = model.getNumeric(CASH_KEY, t - 1) ?? 0;

    const mm0 =
        t === 1
            ? (policy.sweep.opening_mm_balance ?? historyMmAtT0)
            : (model.getNumeric(MM_KEY, t - 1) ?? 0);
    const transfer =
        t === 1 ? (policy.sweep.opening_mm_balance ?? historyMmAtT0) - historyMmAtT0 : 0;

    const apr = aprForMonth(policy, t);
    const interest = interestForMonth(mm0, apr, policy.sweep.enabled);
    // The plug parks this month's MM interest in operating cash (equity is up, r25 not yet).
    const preOperating = pre - interest;
    const generation = roundCents(preOperating - postPrev);

    const monthKey = monthKeyFromT(lastClosedMonth, t);
    const loggedRows = loggedRowsForMonth(context.logged_after_close, monthKey);
    const extraInMonth = oneTimeInMonth(
        policy,
        monthKey,
        context.last_dist_date,
        context.last_closed_month,
    );
    const skipDate = extraInMonth?.date ?? null;
    const nPolicy = policyDatesInMonth(
        policy.distribution.cadence,
        policy.distribution.anchor_date,
        monthKey,
        context.last_dist_date,
        skipDate,
    );
    const policyDist = roundCents((policy.distribution.amount_per_period ?? 0) * nPolicy);
    const extraDist = extraInMonth?.amount ?? 0;

    const tier4Shares = context.tier4_shares ?? {};
    const { lines, source, logged, issues } = buildMonthLines(
        policy,
        t,
        loggedRows,
        policyDist,
        extraDist,
        context,
        tier4Shares,
    );
    // Distributions are whatever was booked to the equity lines, so cash and equity cannot diverge.
    const dist = sumLines(lines);

    for (const [lineKey, amount] of Object.entries(lines)) {
        const prev = model.getNumeric(lineKey, t - 1) ?? 0;
        model.setCell(lineKey, t, prev + amount);
    }
    const accrual = prefAccrualLinesForMonth(policy, t, context);
    if (accrual.issue) {
        issues.push(accrual.issue);
    }
    for (const [lineKey, amount] of Object.entries(accrual.lines)) {
        const prev = model.getNumeric(lineKey, t - 1) ?? 0;
        model.setCell(lineKey, t, prev + amount);
    }

    const avail = preOperating - transfer - F;
    let sweep = 0;
    if (policy.sweep.enabled) {
        if ((policy.sweep.mode ?? 'excess') === 'fixed') {
            const desired = roundCents((policy.sweep.amount_per_period ?? 0) * nPolicy);
            const availableForSweep = roundCents(Math.max(0, avail - dist));
            sweep = roundCents(Math.min(desired, availableForSweep));
        } else {
            const scheduled = context.excess_sweep_by_t?.[t];
            if (scheduled !== undefined) {
                const availableForSweep = roundCents(Math.max(0, avail - dist));
                sweep = roundCents(Math.min(scheduled, availableForSweep));
            } else {
                sweep = roundCents(Math.max(0, avail - dist));
            }
        }
    }
    const mmBalance = mm0 + interest + sweep;
    const operatingCash = preOperating - transfer - dist - sweep;
    model.setCell(MM_KEY, t, mmBalance);
    model.setCell(CASH_KEY, t, operatingCash);

    const breach = operatingCash < F - 0.005;
    const shortfall = roundCents(Math.max(0, F - operatingCash));
    const totalCash = roundCents(operatingCash + mmBalance);

    return {
        month: {
            t,
            month: monthKey,
            n_distributions: loggedRows.length + nPolicy + (extraDist > 0 ? 1 : 0),
            pre_policy_cash: roundCents(pre),
            generation,
            distributions: dist,
            logged_distributions: logged,
            policy_distributions: policyDist,
            one_time_distributions: extraDist,
            initial_transfer: transfer,
            sweep,
            interest,
            apr,
            operating_cash: roundCents(operatingCash),
            mm_balance: roundCents(mmBalance),
            total_cash: totalCash,
            breach,
            shortfall,
            split_source: source,
        },
        splitSource: source,
        issues,
    };
}

export function buildCashPolicyResult(
    policy: CashPolicy,
    months: CashPolicyMonthResult[],
    allWaterfall: boolean,
): CashPolicyResult {
    const effective = effectiveFloor(policy);
    let minOperatingCash = Infinity;
    let minOperatingCashT = 1;
    const breachMonths: number[] = [];
    let maxShortfall = 0;
    let distributions12m = 0;
    let sweep12m = 0;
    let interest12m = 0;

    for (const m of months) {
        if (m.operating_cash < minOperatingCash) {
            minOperatingCash = m.operating_cash;
            minOperatingCashT = m.t;
        }
        if (m.breach) {
            breachMonths.push(m.t);
            if (m.shortfall > maxShortfall) {
                maxShortfall = m.shortfall;
            }
        }
        if (m.t <= 12) {
            distributions12m += m.distributions;
            sweep12m += m.sweep;
            interest12m += m.interest;
        }
    }

    const getMm = (t: number) => months.find((m) => m.t === t)?.mm_balance ?? 0;
    const splitSource = allWaterfall ? 'waterfall' : 'tier4_placeholder';

    return {
        effective_floor: effective,
        months,
        split_source: splitSource,
        summary: {
            min_operating_cash: minOperatingCash === Infinity ? 0 : minOperatingCash,
            min_operating_cash_t: minOperatingCashT,
            breach_months: breachMonths,
            max_shortfall: maxShortfall,
            distributions_12m: roundCents(distributions12m),
            sweep_12m: roundCents(sweep12m),
            interest_12m: roundCents(interest12m),
            mm_balance_t12: getMm(12),
            mm_balance_t24: getMm(24),
            mm_balance_t36: getMm(36),
            split_source: splitSource,
        },
    };
}
